/**
 * useModerationState
 *
 * Realtime subscription to the signed-in user's `moderationState` field on
 * `users/{uid}`. This is the web mirror of the mobile hook — behavior is
 * intentionally the same:
 *
 *   'active'    — normal user; render the app as usual.
 *   'suspended' — reads OK, all abuse-prone writes blocked by Firestore
 *                 rules. Consumer shows a non-dismissible banner.
 *   'banned'    — account terminated; consumer renders a full-screen gate
 *                 instead of the main routes.
 *   'unknown'   — no signed-in user, or the read failed / hasn't landed
 *                 yet. Treated the same as 'active' by the gate (fail
 *                 open — server rules are the source of truth; the client
 *                 gate is defense-in-depth for a better UX).
 *
 * `loading` starts true so the App shell can hold rendering until the
 * first snapshot arrives — otherwise a banned user would see one frame of
 * the app before the gate mounted. It flips to false immediately when
 * there's no authenticated user (nothing to fetch).
 *
 * We also expose `isAdmin` so App.tsx can bypass the gate on `/admin/*`
 * routes without opening a second listener on the same user doc. It's
 * scoped to this hook because the admin-bypass is inseparable from the
 * gating decision.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';

export type ModerationState = 'active' | 'suspended' | 'banned' | 'unknown';

interface Result {
  state: ModerationState;
  isAdmin: boolean;
  loading: boolean;
}

export function useModerationState(): Result {
  const { uid, loading: authLoading } = useAuth();
  const [state, setState] = useState<ModerationState>('unknown');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for auth to settle before deciding what to render. If auth is
    // still resolving we stay in loading — App.tsx will decide whether
    // that means hold-render (signed in) or normal-render (signed out).
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!uid) {
      // Signed out — nothing to gate. 'unknown' means "no signal", which
      // the App treats as pass-through.
      setState('unknown');
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const data = snap.data();
        // Missing doc or missing field → treat as active (matches the
        // Firestore-rules convention where the field being absent means
        // the user is a normal, non-moderated account).
        const next = data?.moderationState as ModerationState | undefined;
        setState(next === 'suspended' || next === 'banned' ? next : 'active');
        setIsAdmin(data?.isAdmin === true);
        setLoading(false);
      },
      (err) => {
        console.warn('[useModerationState] subscription failed', err);
        // Fail open on read errors — don't accidentally lock a legit
        // user out because Firestore hiccuped. Server-side rules are the
        // source of truth; the UI gate is defense-in-depth.
        setState('unknown');
        setIsAdmin(false);
        setLoading(false);
      }
    );
    return unsub;
  }, [uid, authLoading]);

  return { state, isAdmin, loading };
}
